/**
 * Identity forwarding for service-to-service calls.
 *
 * When service A makes a request to service B *on behalf of* a user, B often
 * wants to know who that user is — for attribution/display (dashboards, logs,
 * audit), not authorization. This helper extracts the caller's user token from
 * an inbound request and produces the header(s) to forward it onward.
 *
 * IMPORTANT — this forwards identity, it does NOT delegate authority:
 * - The receiver should read the token for *identity only* (see
 *   `readUnverifiedIdentity` in `@workspace/backend-utils`) and MUST NOT treat a
 *   forwarded token as an authorization to act. Access was already gated at A.
 * - Never refresh a forwarded token (client owns session lifecycle, per
 *   CLAUDE.md). A forwarded token that has expired is still fine for
 *   attribution — the receiver decodes without verifying expiry.
 * - Genuinely userless callers (cron/batch/watcher with no initiating user)
 *   forward nothing; the receiver then attributes by service name only.
 *
 * @example Express
 * ```ts
 * const headers = forwardedIdentityHeaders(req.headers);
 * await llm.post('/v1/chat/completions', body, { headers });
 * ```
 * @example Next.js route handler / Fetch API
 * ```ts
 * const headers = forwardedIdentityHeaders(request.headers);
 * ```
 */

/** The header the receiver reads the forwarded user token from. */
export const FORWARDED_IDENTITY_HEADER = 'authorization';

/**
 * Anything we can read a header value out of: a Fetch `Headers`, an Express
 * `IncomingHttpHeaders` (plain object), or any `{ get(name) }`-shaped source.
 */
export type HeaderSource =
  | Headers
  | Record<string, string | string[] | undefined>
  | { get(name: string): string | null };

function readHeader(source: HeaderSource, name: string): string | undefined {
  // Fetch Headers / anything with a get()
  if (typeof (source as { get?: unknown }).get === 'function') {
    return (source as { get(n: string): string | null }).get(name) ?? undefined;
  }
  // Plain object (Express req.headers) — header names are lower-cased there
  const raw = (source as Record<string, string | string[] | undefined>)[name]
    ?? (source as Record<string, string | string[] | undefined>)[name.toLowerCase()];
  if (Array.isArray(raw)) return raw[0];
  return raw ?? undefined;
}

/**
 * Extract the caller's user token from an inbound request's headers. Looks at
 * `Authorization: Bearer <t>` first, then an `auth_token` cookie. Returns the
 * bare token (no scheme), or undefined when there's no user token to forward.
 */
export function extractInboundToken(source: HeaderSource): string | undefined {
  const auth = readHeader(source, 'authorization');
  if (auth?.startsWith('Bearer ')) {
    const t = auth.slice(7).trim();
    if (t) return t;
  }
  const cookie = readHeader(source, 'cookie');
  if (cookie) {
    const match = cookie.match(/(?:^|;\s*)auth_token=([^;]*)/);
    if (match?.[1]) return decodeURIComponent(match[1]);
  }
  return undefined;
}

/**
 * Build the header object to forward the caller's identity onward. Returns an
 * empty object when there's no user token — spread it unconditionally:
 *
 *   { headers: { ...forwardedIdentityHeaders(req.headers), 'x-service': 'foo' } }
 *
 * Pass an explicit `token` (e.g. a PAT held by a daemon that has no inbound
 * request) via `{ token }` to forward a known identity instead of extracting.
 */
export function forwardedIdentityHeaders(
  source: HeaderSource | { token: string | undefined | null },
): Record<string, string> {
  const token =
    source && typeof source === 'object' && 'token' in source
      ? source.token
      : extractInboundToken(source as HeaderSource);
  return token ? { [FORWARDED_IDENTITY_HEADER]: `Bearer ${token}` } : {};
}

/**
 * The header carrying a user token for ATTRIBUTION ONLY, when the call itself
 * authenticates as a service. The receiver may read it with an expiry-tolerant
 * authenticity check (`verifyIgnoringExpiry`) to label the work, and must never
 * authorize on it (adr-0015 amendment, 2026-10-07).
 */
export const ATTRIBUTION_IDENTITY_HEADER = 'x-forwarded-identity';

/** True when `token`'s `exp` is less than `seconds` away, or it has no readable `exp`. */
export function tokenExpiresWithin(token: string, seconds: number, nowMs: number = Date.now()): boolean {
  try {
    const payload = token.split('.')[1];
    if (!payload) return true;
    // atob, not Buffer: this package also ships a browser build. `exp` is ASCII,
    // so a binary-string decode is enough.
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: unknown };
    return typeof json.exp !== 'number' || json.exp * 1000 - nowMs < seconds * 1000;
  } catch {
    return true;
  }
}

/**
 * Credential headers for a backend call into a gated first-party service
 * (adr-0015 amendment; dl-0009 service tokens).
 *
 * - **A user token that is still valid** for `minRemainingSeconds` is forwarded
 *   as the Bearer, so the receiver authorizes the work as that user and applies
 *   their scope and quota.
 * - **Otherwise the service's own token** is the Bearer, so the call runs as the
 *   service. That covers no user (cron, daemon, script), or a user token about
 *   to age out mid-batch. An aged-out user token still goes along in
 *   {@link ATTRIBUTION_IDENTITY_HEADER} so the work stays attributed.
 * - **Neither:** returns `{}`. The call is credential-less, and a receiver
 *   enforcing credentials will refuse it.
 *
 * Only call this for first-party receivers you choose. Never use it for a
 * caller-supplied URL: it hands that host your service credential.
 */
export function serviceCallHeaders(options: {
  userToken?: string | null;
  serviceToken?: string | null;
  minRemainingSeconds?: number;
}): Record<string, string> {
  const { userToken, serviceToken, minRemainingSeconds = 60 } = options;
  if (userToken && !tokenExpiresWithin(userToken, minRemainingSeconds)) {
    return { [FORWARDED_IDENTITY_HEADER]: `Bearer ${userToken}` };
  }
  if (serviceToken) {
    return {
      [FORWARDED_IDENTITY_HEADER]: `Bearer ${serviceToken}`,
      ...(userToken ? { [ATTRIBUTION_IDENTITY_HEADER]: `Bearer ${userToken}` } : {}),
    };
  }
  return userToken ? { [FORWARDED_IDENTITY_HEADER]: `Bearer ${userToken}` } : {};
}
